// src/utils/api.js
import axios from "axios";
import { getMsal } from "./msal";               // ⬅️ read the instance created in index.js
import { loginRequest, forgetCachedAccount } from "../authConfig";
import { savePostAuthRedirect } from "./authRedirect";

export const API_BASE = process.env.REACT_APP_API_BASE;

// The memory-plugin endpoints (/me/tree-link, /me/tree) live on the dedicated memory backend.
// On prod, studio's API_BASE is the analytics service which doesn't carry them, so target the
// memory backend explicitly (its CORS allows the studio origin). Locally/dev, fall back to
// API_BASE (the dev backend does carry them). An explicit env var wins if ever set.
// Our own hosts only: the apex or a subdomain of assertion-ai.com — a suffix test without the dot
// would also accept "evilassertion-ai.com".
export function isStudioHost(hostname) {
  const h = String(hostname || "").toLowerCase();
  return h === "assertion-ai.com" || h.endsWith(".assertion-ai.com");
}
const onStudio = typeof window !== "undefined" && isStudioHost(window.location.hostname);
export const MEMORY_API_BASE =
  process.env.REACT_APP_MEMORY_API_BASE || (onStudio ? "https://memory.assertion-ai.com" : API_BASE);

// Two memory services, same code, different stores. The PLUGIN fleet (Claude Code, Cursor, Codex)
// writes Firestore through memory.assertion-ai.com — that is what /memory shows. The AGENT writes
// Postgres through its own function — so every /agent/* page reads THAT one, or it would show a
// memory the agent never wrote to. Identity, spaces and usage are shared, so either answers those.
// Locally both resolve to the dev backend. An explicit env var wins if ever set.
export const AGENT_API_BASE =
  process.env.REACT_APP_AGENT_API_BASE || (onStudio ? "https://assertion-memory-agent-jbdf42voqq-uc.a.run.app" : API_BASE);
export function memoryBase(pathname = (typeof window !== "undefined" ? window.location.pathname : "")) {
  return pathname === "/agent" || pathname.startsWith("/agent/") ? AGENT_API_BASE : MEMORY_API_BASE;
}

// --- Auth helpers ---

// The account to mint tokens for. getAllAccounts()[0] is NOT reliable: once a browser has
// more than one account cached (a second sign-in, a leftover federated entry), index 0 can
// be an account the current session never authenticated. Silent then fails forever for it,
// and the old code answered that by redirecting — over and over. The active account is the
// one index.js pins from handleRedirectPromise/LOGIN_SUCCESS.
function activeAccount(msal) {
  return msal.getActiveAccount() || msal.getAllAccounts()[0] || null;
}

// Does this failure genuinely require the user to interact, i.e. is a full-page sign-in
// redirect the correct response? Only these. A network blip, a timeout, or MSAL cache noise
// must NOT trigger a redirect: the old blanket `catch` treated every rejection as
// "needs interaction", which is how one transient failure became an endless
// studio → CIAM → studio bounce.
function needsInteractiveAuth(e) {
  if (e?.name === "InteractionRequiredAuthError") return true;
  const code = e?.errorCode || "";
  return code === "login_required"
      || code === "consent_required"
      || code === "interaction_required"
      || code === "no_tokens_found"      // silent cache miss with no refresh token
      || code === "no_account_error";
}

// MSAL's own "a redirect is already under way" signal. Starting a second interaction here is
// exactly what corrupts the cached interaction state, so this case must never redirect.
function interactionAlreadyRunning(e) {
  return (e?.errorCode || "") === "interaction_in_progress"
      || /interaction_in_progress/.test(e?.message || "");
}

async function acquireBearer() {
  const msal = getMsal();
  const account = activeAccount(msal);
  if (!account) throw new Error("No logged-in user");

  const request = { ...loginRequest, account };

  try {
    const res = await msal.acquireTokenSilent(request);
    return res.accessToken || res.idToken;
  } catch (e) {
    if (interactionAlreadyRunning(e)) {
      // Someone else's redirect owns this page load. Fail this caller; the in-flight
      // interaction will produce a token (and a stale flag is cleared at boot).
      throw new Error("AUTH_INTERACTION_IN_PROGRESS");
    }
    if (!needsInteractiveAuth(e)) {
      // Transient. Surface it so the caller can retry or degrade — never redirect.
      console.warn("[AUTH] silent token failed transiently; not redirecting:", e?.errorCode || e);
      throw e;
    }
    console.warn("[AUTH] interaction required; redirecting for sign-in:", e?.errorCode || e);
    savePostAuthRedirect();          // …so we come back HERE, not to "/"
    await forgetCachedAccount(msal);               // its session is gone; naming it misroutes Entra
    await msal.acquireTokenRedirect({ ...loginRequest }); // navigates; nothing below runs
    // Throw so callers never assume a token was returned.
    throw new Error("REDIRECTING_FOR_AUTH");
  }
}

// Single-flight. "/" mounts several independent fetchers that each call getBearer in the same
// tick; without this they each independently decide to redirect, MSAL rejects all but one with
// interaction_in_progress, and the interrupted redirects poison the cached interaction state.
// One shared in-flight promise means at most ONE interaction per page load.
let _bearerInFlight = null;

async function getBearer() {
  if (!_bearerInFlight) {
    _bearerInFlight = acquireBearer();
    // Clear on settle so the next call retries rather than replaying a stale rejection.
    _bearerInFlight.catch(() => {}).then(() => { _bearerInFlight = null; });
  }
  return _bearerInFlight;
}

// Silent-only token: NEVER triggers a full-page sign-in redirect. Throws
// "INTERACTION_REQUIRED" if it can't get a token quietly, so the caller can show an
// in-page "Sign in" (popup) instead of bouncing the whole app to the login redirect.
async function getBearerSilent() {
  const msal = getMsal();
  const account = activeAccount(msal);
  if (!account) throw new Error("INTERACTION_REQUIRED");
  try {
    const res = await msal.acquireTokenSilent({ ...loginRequest, account });
    return res.accessToken || res.idToken;
  } catch (e) {
    throw new Error("INTERACTION_REQUIRED");
  }
}

// Test seam: reset the single-flight latch between cases.
export function __resetBearerInFlight() {
  _bearerInFlight = null;
}

// --- Impersonation: admins can act on behalf of any org ---
// Updated by ImpersonationContext.setActingOrg(); persisted via localStorage.
let _actingOrgId = null;
try {
  const stored = typeof window !== "undefined" ? window.localStorage.getItem("acting_org_id") : null;
  if (stored) _actingOrgId = stored;
} catch (_) {}

export function setActingOrgId(orgId) {
  _actingOrgId = orgId || null;
  try {
    if (typeof window !== "undefined") {
      if (orgId) window.localStorage.setItem("acting_org_id", orgId);
      else window.localStorage.removeItem("acting_org_id");
    }
  } catch (_) {}
  // Changing org invalidates any per-user selection (workspace + acting-user are org-specific).
  setActingWorkspaceId(null);
  setActingUserId(null);
}

export function getActingOrgId() {
  return _actingOrgId;
}

// --- Acting WORKSPACE (memory-product only; analytics has no workspace concept) ---
// Lets an admin impersonate a specific user within an isolated org (org__<userslug>). Sent ONLY
// on the memory calls below (?ws=), never on analytics requests. Persisted via localStorage.
let _actingWorkspaceId = null;
try {
  const stored = typeof window !== "undefined" ? window.localStorage.getItem("acting_workspace_id") : null;
  if (stored) _actingWorkspaceId = stored;
} catch (_) {}

export function setActingWorkspaceId(ws) {
  _actingWorkspaceId = ws || null;
  try {
    if (typeof window !== "undefined") {
      if (ws) window.localStorage.setItem("acting_workspace_id", ws);
      else window.localStorage.removeItem("acting_workspace_id");
    }
  } catch (_) {}
}

export function getActingWorkspaceId() {
  return _actingWorkspaceId;
}

// Query suffix carrying the acting workspace for MEMORY endpoints only (honored server-side
// solely for global admins who are impersonating; ignored otherwise).
function actingWsQuery() {
  return _actingWorkspaceId ? `?ws=${encodeURIComponent(_actingWorkspaceId)}` : "";
}

// --- Acting USER (analytics per-user "view as"; admin-only, feature-flagged) ---
// Lets a global admin view a specific USER's data within the acting org (e.g. a teammate).
// Sent as X-Acting-User (the target uid) on analytics requests ALONGSIDE X-Acting-Org, honored
// server-side SOLELY for global admins; ignored/rejected otherwise. Purely ADDITIVE: when unset
// (the only state a non-admin can ever be in) requests are byte-identical to today. WRITES under
// this header are permitted server-side ONLY when the target is a flagged test account; a real
// customer is structurally read-only. Persisted via localStorage.
let _actingUserId = null;
try {
  const stored = typeof window !== "undefined" ? window.localStorage.getItem("acting_user_id") : null;
  if (stored) _actingUserId = stored;
} catch (_) {}

export function setActingUserId(uid) {
  _actingUserId = uid || null;
  try {
    if (typeof window !== "undefined") {
      if (uid) window.localStorage.setItem("acting_user_id", uid);
      else window.localStorage.removeItem("acting_user_id");
    }
  } catch (_) {}
}

export function getActingUserId() {
  return _actingUserId;
}

export async function authHeaders(extra = {}) {
  const token = await getBearer();
  const headers = { Authorization: `Bearer ${token}`, ...extra };
  if (_actingOrgId) headers["X-Acting-Org"] = _actingOrgId;
  if (_actingUserId) headers["X-Acting-User"] = _actingUserId;
  return headers;
}

// Single place to attach auth to axios calls
function withAuth(config, extraHeaders = {}) {
  return (async () => {
    const headers = await authHeaders(extraHeaders);
    return { ...config, headers };
  })();
}

// Like withAuth, but silent-only — throws "INTERACTION_REQUIRED" instead of redirecting,
// so a page can offer an in-page sign-in rather than bouncing the whole app.
function withAuthSilent(config, extraHeaders = {}) {
  return (async () => {
    const token = await getBearerSilent();
    const headers = { Authorization: `Bearer ${token}`, ...extraHeaders };
    if (_actingOrgId) headers["X-Acting-Org"] = _actingOrgId;
    if (_actingUserId) headers["X-Acting-User"] = _actingUserId;
    return { ...config, headers };
  })();
}

// ============ Run Usage ============
export async function getRunUsage() {
  const { data } = await axios.get(`${API_BASE}/run_usage`, await withAuth({ timeout: 10_000 }));
  return data; // { tier, used, cap }
}


// ============ Settings ============
export async function getUserSettings() {
  const { data } = await axios.get(`${API_BASE}/settings`, await withAuth());
  return data;
}

export async function saveUserSettings(settings) {
  const { data } = await axios.put(
    `${API_BASE}/settings`,
    settings,
    await withAuth()
  );
  return data;
}


// ============ Memory plugin: self-serve API key ============
// Returns the signed-in user's own api_key (auto-provisions account + org on first call).
export async function getMyApiKey() {
  // First call for a brand-new user triggers inline account/org provisioning
  // server-side, so allow more headroom than a normal authed GET.
  const { data } = await axios.get(`${API_BASE}/me/api-key`, await withAuth({ timeout: 30_000 }));
  return data; // { api_key }
}

export async function rotateMyApiKey() {
  const { data } = await axios.post(`${API_BASE}/me/api-key/rotate`, {}, await withAuth({ timeout: 15_000 }));
  return data; // { api_key }
}

// Query for the /me/* view endpoints. Two mutually-exclusive channels:
//  - admin impersonation (?ws, actingWsQuery) — global-admin-only, wins when acting;
//  - member VIEW (?space) — browse a team space the caller belongs to, read-only,
//    validated server-side by membership. Never sent while impersonating (the space
//    switcher is hidden then). Extra params (e.g. limit) append cleanly.
function meViewQuery(space, extra = "") {
  const parts = [];
  const ws = actingWsQuery();               // "?ws=..." or ""
  if (ws) parts.push(ws.slice(1));
  else if (space) parts.push(`space=${encodeURIComponent(space)}`);
  if (extra) parts.push(extra);
  return parts.length ? `?${parts.join("&")}` : "";
}

// Mints a short-lived signed link to the user's memory-tree browser, so we can open it
// without the Basic-Auth prompt (the studio session authorizes the mint). Pass `space`
// to mint a member-validated link to a team space's tree (view-only — routing untouched).
export async function getMyTreeLink(space) {
  // silent-only: if the session needs interaction, the /memory page shows a Sign-in button
  // (popup) instead of triggering a full-page redirect loop.
  const { data } = await axios.get(`${memoryBase()}/me/tree-link${meViewQuery(space)}`, await withAuthSilent({ timeout: 15_000 }));
  return data; // { url, expires_at }
}

// The receipts feed ("how memory's helping"): per-turn surfaced∩cited assist events for the
// caller's personal tree, or a team space they belong to when `space` is passed.
export async function getMyAssistLog(space) {
  const { data } = await axios.get(`${memoryBase()}/me/assist-log${meViewQuery(space)}`, await withAuthSilent({ timeout: 15_000 }));
  return data; // { totals, events (newest-first), nodes }
}

// The Recent-capture stream: the newest captured claims (takeaway + the question each answers),
// newest-first, for the caller's personal tree or a viewed team space.
export async function getMyRecentCaptures(limit = 15, space) {
  const q = meViewQuery(space, `limit=${limit}`);
  const { data } = await axios.get(`${memoryBase()}/me/recent-captures${q}`, await withAuthSilent({ timeout: 15_000 }));
  return data; // { items: [{id, claim, subject_key, created_at, repo, level}] }
}

// Admin-only: list an org's memory workspaces (for the /memory user switcher). Memory-scoped, so it
// lives on the MEMORY backend (shipped in memory-v0.78) — MUST target MEMORY_API_BASE, not API_BASE.
// On prod, API_BASE is the analytics service, which does NOT carry memory routes (same reason
// getMyTreeLink/getMyAssistLog use MEMORY_API_BASE); locally both resolve to the dev backend.
export async function listAdminWorkspaces(orgId) {
  const { data } = await axios.get(`${memoryBase()}/admin/workspaces?org=${encodeURIComponent(orgId)}`, await withAuth({ timeout: 15_000 }));
  return data; // { org, workspaces: [{workspace, owner_email}] }
}

// --- Team spaces (memory product, user-facing — NOT the admin impersonation channel) ---
// Self-serve shared-memory workspaces: list mine, create one, manage members, and pick the
// account-level ACTIVE space (the /memory space switcher). All memory-scoped, so they target
// MEMORY_API_BASE. Deliberately no ?ws= here: the switcher writes `active_workspace` server-side
// and the server routes every subsequent read/capture — ?ws= + X-Acting-Org stay admin-only.
export async function listMyWorkspaces() {
  const { data } = await axios.get(`${memoryBase()}/me/workspaces`, await withAuthSilent({ timeout: 15_000 }));
  return data; // { home, active, spaces: [{workspace, name, owner_email, is_owner (a co-owner), co_owners, can_step_down, members: [{uid, email, is_co_owner, removable, can_make_co_owner}], member_count}] }
}

export async function createWorkspace(name) {
  const { data } = await axios.post(`${memoryBase()}/me/workspaces`, { name }, await withAuth({ timeout: 15_000 }));
  return data; // the created space payload
}

export async function setActiveWorkspace(workspace) {
  // workspace: a space id to activate, or null/"" to switch back to Personal.
  const { data } = await axios.put(`${memoryBase()}/me/workspaces/active`, { workspace: workspace || null }, await withAuth({ timeout: 15_000 }));
  return data; // { active }
}

export async function addWorkspaceMember(workspace, email) {
  const { data } = await axios.post(`${memoryBase()}/me/workspaces/${encodeURIComponent(workspace)}/members`, { email }, await withAuth({ timeout: 15_000 }));
  return data; // updated space payload
}

// Inviting to a space: the email is where the invite goes, not an account lookup, so it works for
// anyone, whatever they sign up with; they join from the link. No email: a link to copy and send.
export async function inviteToSpace(workspace, email) {
  const { data } = await axios.post(`${memoryBase()}/me/spaces/${encodeURIComponent(workspace)}/invites`,
    email ? { email } : {}, await withAuth({ timeout: 15_000 }, { "Content-Type": "application/json" }));
  return data; // { url, invite_id, expires_at, email, emailed }
}
export async function listSpaceInvites(workspace) {
  const { data } = await axios.get(`${memoryBase()}/me/spaces/${encodeURIComponent(workspace)}/invites`, await withAuth({ timeout: 15_000 }));
  return data?.invites || []; // [{ invite_id, email, created_at, expires_at }]
}
export async function withdrawSpaceInvite(workspace, inviteId) {
  const { data } = await axios.delete(`${memoryBase()}/me/spaces/${encodeURIComponent(workspace)}/invites/${encodeURIComponent(inviteId)}`, await withAuth({ timeout: 15_000 }));
  return data;
}

export async function removeWorkspaceMember(workspace, uid) {
  const { data } = await axios.delete(`${memoryBase()}/me/workspaces/${encodeURIComponent(workspace)}/members/${encodeURIComponent(uid)}`, await withAuth({ timeout: 15_000 }));
  return data; // updated space payload
}

// Co-owners run a shared space together. Any co-owner makes a member one; a co-owner can only
// step down themself — co-owners can't demote each other.
export async function addWorkspaceCoOwner(workspace, uid) {
  const { data } = await axios.post(`${memoryBase()}/me/workspaces/${encodeURIComponent(workspace)}/co-owners`, { uid }, await withAuth({ timeout: 15_000 }));
  return data; // updated space payload
}

export async function stepDownWorkspaceCoOwner(workspace) {
  const { data } = await axios.delete(`${memoryBase()}/me/workspaces/${encodeURIComponent(workspace)}/co-owners/me`, await withAuth({ timeout: 15_000 }));
  return data; // updated space payload
}

// Teammate directory for the add-member Autocomplete: email + display name of the caller's
// org co-members. Memory-side twin of the analytics /orgs/{id}/users — deliberately NOT that
// endpoint, which lives on the analytics service (the API_BASE-vs-MEMORY_API_BASE trap).
export async function listMemoryOrgUsers() {
  const { data } = await axios.get(`${memoryBase()}/me/org-users`, await withAuthSilent({ timeout: 15_000 }));
  return data; // { users: [{email, display_name}] }
}


// ============ Runs ============
export async function createRunMultipart({ file, dict_id, user_problem_stat, dataset, platform, project, business_output_mode }) {
  if (!file && !dict_id) throw new Error("Provide either a dictionary file or a dict_id.");

  const form = new FormData();
  if (file) form.append("file", file);
  if (dict_id) form.append("dict_id", dict_id);
  form.append("user_problem_stat", user_problem_stat);
  form.append("dataset", dataset);
  form.append("platform", platform);
  if (project) form.append("project", project);
  if (business_output_mode === "none" || business_output_mode === "standard") {
    form.append("business_output_mode", business_output_mode);
  }

  try {
    const { data } = await axios.post(`${API_BASE}/runs`, form, await withAuth({ timeout: 30_000 }));
    const runId = data.runId || data.run_id || data.id;
    return { ...data, runId };
  } catch (err) {
    const status = err?.response?.status;
    if (dict_id || (status !== 404 && status !== 405)) throw enrichAxiosError("[CREATE RUN ERROR]", err);
  }

  // Legacy fallback
  try {
    const { data } = await axios.post(`${API_BASE}/`, form, await withAuth({ timeout: 15 * 60 * 1000 }));
    return { legacy: true, data };
  } catch (err) {
    throw enrichAxiosError("[LEGACY RUN ERROR]", err);
  }
}

export async function getRun(runId) {
  if (!runId) throw new Error("runId is required");
  const { data } = await axios.get(`${API_BASE}/runs/${encodeURIComponent(runId)}`, await withAuth({ timeout: 30_000 }));
  return data;
}

/** Fetch a run-scoped GCS asset via the API (for images under runs/{user}/{runId}/...). */
export async function fetchRunOutputBlobUrl(runId, { url, path } = {}) {
  if (!runId) throw new Error("runId is required");
  const params = new URLSearchParams();
  if (url) params.set("url", url);
  else if (path) params.set("path", path);
  else throw new Error("url or path is required");
  const res = await fetch(`${API_BASE}/runs/${encodeURIComponent(runId)}/output-file?${params}`, {
    headers: await authHeaders(),
  });
  if (!res.ok) {
    const t = await res.text().catch(() => "");
    throw new Error(t?.slice(0, 200) || `Failed to load file (${res.status})`);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

export function revokeRunOutputBlobUrl(url) {
  if (url && String(url).startsWith("blob:")) {
    try {
      URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    }
  }
}

export async function listRuns() {
  const { data } = await axios.get(`${API_BASE}/runs`, await withAuth({ timeout: 15_000 }));
  return data; // [{ id, ... }]
}

export async function pollRun(runId, { intervalMs = 3000, maxMs = 20 * 60 * 1000, onUpdate } = {}) {
  const start = Date.now();
  let last = null;
  while (true) {
    const data = await getRun(runId);
    if (onUpdate && data?.status !== last) {
      last = data?.status;
      onUpdate(data);
    }
    if (!data?.status) throw new Error("Malformed run status response");
    if (["succeeded", "failed", "cancelled"].includes(data.status)) return data;
    if (Date.now() - start > maxMs) throw new Error("Polling timed out");
    await delay(intervalMs);
  }
}
export { createRunMultipart as runAutomationMultipart };


export async function rerunRun(runId, params = {}) {
  if (!runId) throw new Error("runId is required");

  const { data } = await axios.post(
    `${API_BASE}/runs/${encodeURIComponent(runId)}/rerun`,
    { rerun: true, ...params },
    await withAuth({ timeout: 30_000 })
  );

  return data; // { ok: true } or { runId, status }
}


export async function deleteRun(runId) {
  if (!runId) throw new Error("runId is required");

  const { data } = await axios.post(
    `${API_BASE}/runs/${encodeURIComponent(runId)}/delete`,
    {},  // empty body
    await withAuth({ timeout: 15_000 })
  );

  return data; // { ok: true }
}

// ============ Dictionaries ============
export async function listDictionaries() {
  const { data } = await axios.get(`${API_BASE}/dictionaries`, await withAuth({ timeout: 15_000 }));
  return data;
}

export async function createDictionary({ file, name }) {
  if (!file) throw new Error("Dictionary file is required");
  const form = new FormData();
  form.append("file", file);
  if (name) form.append("name", name);
  const { data } = await axios.post(`${API_BASE}/dictionaries`, form, await withAuth({ timeout: 60_000 }));
  return data;
}

export async function getDictionary(dictId) {
  const { data } = await axios.get(`${API_BASE}/dictionaries/${encodeURIComponent(dictId)}`, await withAuth({ timeout: 10_000 }));
  return data;
}

export async function updateDictionary(dictId, { name }) {
  const { data } = await axios.put(
    `${API_BASE}/dictionaries/${encodeURIComponent(dictId)}`,
    { name },
    await withAuth({ timeout: 10_000 }, { "Content-Type": "application/json" })
  );
  return data;
}

export async function deleteDictionary(dictId) {
  const { data } = await axios.delete(`${API_BASE}/dictionaries/${encodeURIComponent(dictId)}`, await withAuth({ timeout: 20_000 }));
  return data;
}

export async function createDictionaryFromText(name, content) {
  const safeName = (name || "dictionary").trim();
  const file = new File(
    [content ?? ""],
    `${safeName.replace(/\s+/g, "_")}.yaml`,   
    { type: "text/yaml; charset=utf-8" }
  );
  const form = new FormData();
  form.append("file", file);
  form.append("name", safeName);
  const { data } = await axios.post(`${API_BASE}/dictionaries`, form, await withAuth({ timeout: 60_000 }));
  return data;
}

export async function getDictionaryWithContent(dictId) {
  const { data } = await axios.get(
    `${API_BASE}/dictionaries/${encodeURIComponent(dictId)}?include=content`,
    await withAuth({ timeout: 15_000 })
  );
  return data;
}

export async function updateDictionaryFields(dictId, patch) {
  const { data } = await axios.patch(
    `${API_BASE}/dictionaries/${encodeURIComponent(dictId)}`,
    patch,
    await withAuth({ timeout: 20_000 })
  );
  return data;
}

export async function getDictionaryContent(dictId) {
  const { data } = await axios.get(
    `${API_BASE}/dictionaries/${encodeURIComponent(dictId)}/content`,
    await withAuth({ timeout: 15_000, responseType: "text", transformResponse: v => v })
  );
  return typeof data === "string" ? data : String(data ?? "");
}

export async function saveDictionaryContent(dictId, text) {
  const url = `${API_BASE}/dictionaries/${encodeURIComponent(dictId)}/content`;
  const { data } = await axios.put(
    url,
    text ?? "",
    await withAuth({ timeout: 15_000 }, { "Content-Type": "text/plain; charset=utf-8" })
  );
  return data;
}

export async function createRunWithDictId({ dictId, dict_name, problem_statement, dataset, platform, project, run_name, business_output_mode, project_id }) {
  const form = new FormData();
  form.append("dict_id", dictId);
  if (dict_name) form.append("dict_name", dict_name);
  form.append("problem_statement", problem_statement);
  form.append("dataset", dataset);
  form.append("platform", platform);
  form.append("project", project);
  form.append("run_name", run_name);
  if (business_output_mode === "none" || business_output_mode === "standard") {
    form.append("business_output_mode", business_output_mode);
  }
  if (project_id) form.append("project_id", project_id);
  const { data } = await axios.post(`${API_BASE}/runs`, form, await withAuth({ timeout: 30_000 }));
  const runId = data.runId || data.run_id || data.id;
  return { ...data, runId };
}

// ============ Run data (BigQuery table viewer) ============
/**
 * Fetch paginated rows from a run's BigQuery table.
 * @param {string} runId
 * @param {object} opts - { table, limit, offset, sort, order, ...filters }
 * @returns {Promise<{ rows: array, total: number, columns?: string[] }>}
 */
export async function getRunData(runId, opts = {}) {
  if (!runId) throw new Error("runId is required");
  const {
    table,
    limit = 100,
    offset = 0,
    sort,
    order,
    columns: columnProjection,
    ...filters
  } = opts;
  if (!table) throw new Error("table is required");
  const params = new URLSearchParams({ table, limit: String(limit), offset: String(offset) });
  if (sort != null) params.set("sort", String(sort));
  if (order != null) params.set("order", String(order));
  if (Array.isArray(columnProjection) && columnProjection.length > 0) {
    params.set("columns", columnProjection.join(","));
  }
  Object.entries(filters).forEach(([k, v]) => {
    if (v != null && v !== "") params.set(k, String(v));
  });
  const { data } = await axios.get(
    `${API_BASE}/runs/${encodeURIComponent(runId)}/data?${params.toString()}`,
    await withAuth({ timeout: 30_000 })
  );
  return data;
}

/**
 * The Stock Summary Dashboard's rows, read from BigQuery server-side and cached —
 * not extracted from a pre-rendered HTML report.
 *
 * Returns { packed, data_as_of, rows, generated_at }; `packed` is gzip+base64 JSON
 * of { summaryCols, detailCols, summary, detail, pool, industries }.
 * @param {string} projectId
 * @param {{ fresh?: boolean }} [opts] fresh:true queries BigQuery now (slow, but
 *   cannot serve anything stale) — what an explicit refresh wants.
 */
export async function getProjectDashboardData(projectId, opts = {}) {
  if (!projectId) throw new Error("projectId is required");
  const qs = opts.fresh ? "?fresh=1" : "";
  const { data } = await axios.get(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/dashboard-data${qs}`,
    await withAuth({ timeout: opts.fresh ? 120_000 : 60_000 })
  );
  return data;
}

/**
 * The model-ranking rows for a run, served from the cached payload on the run
 * document rather than a live BigQuery query.
 *
 * Returns { packed, as_of, rows, generated_at } where `packed` is gzip+base64 JSON
 * of { cols, rows, total, as_of } — the same transport the stock dashboard uses, so
 * a page view costs no BigQuery job.
 * @param {string} runId
 */
export async function getRunRanking(runId) {
  if (!runId) throw new Error("runId is required");
  const { data } = await axios.get(
    `${API_BASE}/runs/${encodeURIComponent(runId)}/ranking`,
    await withAuth({ timeout: 60_000 })   // a cold cache builds inline, once
  );
  return data;
}

/**
 * List table names in the run's BigQuery dataset.
 * @param {string} runId
 * @returns {Promise<{ tables: string[] }>}
 */
export async function getRunTables(runId) {
  if (!runId) throw new Error("runId is required");
  const { data } = await axios.get(
    `${API_BASE}/runs/${encodeURIComponent(runId)}/tables`,
    await withAuth({ timeout: 15_000 })
  );
  return data;
}

/**
 * Upload a CSV into BigQuery for this run: dataset run_{runId} (or existing bigquery_dataset), table user_input_data.
 * @param {string} runId
 * @param {File} file
 */
export async function uploadUserInputCsv(runId, file) {
  if (!runId) throw new Error("runId is required");
  if (!file) throw new Error("file is required");
  const form = new FormData();
  form.append("file", file);
  const apiKey = process.env.REACT_APP_API_KEY;
  const extraHeaders = apiKey ? { "X-API-Key": apiKey } : {};
  try {
    const { data } = await axios.post(
      `${API_BASE}/runs/${encodeURIComponent(runId)}/user-input-data`,
      form,
      await withAuth({ timeout: 120_000 }, extraHeaders)
    );
    return data;
  } catch (err) {
    throw enrichAxiosError("[UPLOAD USER CSV]", err);
  }
}

// ============ Insights (business view) ============
export async function getInsights(runId) {
  const bearer = await getBearer();
  const url = `${API_BASE}/runs/${encodeURIComponent(runId)}/insights`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${bearer}` } });
  if (!res.ok) throw new Error(`GET insights failed ${res.status}`);
  return await res.json();
}

// ============ Decision output (business recommendation JSON) ============
// Same transport as getInsights: fetch + res.json() so a JSON *string* body becomes a JS string reliably.
export async function getDecisionOutput(runId) {
  const bearer = await getBearer();
  const url = `${API_BASE}/runs/${encodeURIComponent(runId)}/decision_output`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${bearer}` } });
  if (!res.ok) throw new Error(`GET decision_output failed ${res.status}`);
  return await res.json();
}

// ============ Report HTML (optional GCS report.html for a run) ============
// Returns the HTML string if report.html exists under the run's GCS prefix, null if not found.
export async function getRunReport(runId) {
  const bearer = await getBearer();
  const url = `${API_BASE}/runs/${encodeURIComponent(runId)}/output-file?path=report.html`;
  // cache: 'no-store' bypasses the browser HTTP cache so edits/deletes on GCS are always reflected.
  const res = await fetch(url, { headers: { Authorization: `Bearer ${bearer}` }, cache: "no-store" });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`GET report failed ${res.status}`);
  return await res.text();
}

// ============ Notebook (fetch) ============
export async function getNotebook(runId, { format = "json" } = {}) {
  const bearer = await getBearer();
  const url = `${API_BASE}/runs/${encodeURIComponent(runId)}/notebook?format=${format}`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${bearer}` } });
  if (!res.ok) throw new Error(`GET notebook failed ${res.status}: ${(await res.text()).slice(0, 300)}`);
  return await res.json();
}

// Download the v2 ML run's assembled notebook (.ipynb) via an authenticated
// fetch and trigger a browser save. Plain <a href> won't work — the endpoint
// requires the bearer token. Server-side sanitizes infrastructure names
// (project, scratch dataset) before returning.
export async function downloadV2RunNotebook(runId) {
  const bearer = await getBearer();
  const url = `${API_BASE}/v2/runs/${encodeURIComponent(runId)}/notebook`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${bearer}` } });
  if (!res.ok) {
    const detail = (await res.text()).slice(0, 300);
    throw new Error(`Notebook download failed (${res.status}): ${detail}`);
  }
  const blob = await res.blob();
  const dlUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = dlUrl;
  a.download = `model_${runId.slice(0, 8)}.ipynb`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(dlUrl), 250);
}

export async function saveNotebook(runId, nbJson) {
  const bearer = await getBearer();
  const url = `${API_BASE}/runs/${encodeURIComponent(runId)}/notebook`;
  const res = await fetch(url, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${bearer}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(nbJson),
  });
  if (!res.ok) throw new Error(`PUT notebook failed ${res.status}: ${(await res.text()).slice(0, 300)}`);
  return await res.json();
}

export async function getDataRefreshTimestamp(runId) {
  if (!runId) throw new Error("runId is required");
  const { data } = await axios.get(
    `${API_BASE}/runs/${encodeURIComponent(runId)}/data-refresh-timestamp`,
    await withAuth({ timeout: 10_000 })
  );
  return data; // { timestamp: "ISO string" | null }
}

// --- utils ---
function delay(ms) { return new Promise(res => setTimeout(res, ms)); }

function enrichAxiosError(prefix, err) {
  const detail = err?.response
    ? `HTTP ${err.response.status} ${err.response.statusText}\n${safeStringify(err.response.data, 400)}`
    : (err?.message || String(err));
  console.error(prefix, {
    message: err?.message,
    code: err?.code,
    name: err?.name,
    responseStatus: err?.response?.status,
    responseHeaders: err?.response?.headers,
  });
  return new Error(`${prefix}: ${detail}`);
}
function safeStringify(obj, maxLen = 400) {
  try { const s = typeof obj === "string" ? obj : JSON.stringify(obj); return s?.slice(0, maxLen); }
  catch { return String(obj).slice(0, maxLen); }
}

// ============ Agent plan + usage (memory side) ============
// The memory-native agent's plan and this week's usage, plus the memory capture cap and the
// user's funnel so far. Lives on the MEMORY backend, which reads the gateway with the user's key.
// Everything the shell needs to say WHERE Assertion is running: the agent's own installs
// (clients) and the plugin clients, each with version, when it was last seen and whether it is
// behind, plus the latest version of each. Also carries this week's per-repo activity. Read-only.
export async function getAgentHome() {
  const { data } = await axios.get(`${memoryBase()}/me/agent-home`, await withAuthSilent({ timeout: 15_000 }));
  return data; // { clients: [{client, version, at, outdated}], plugin_clients: [...], latest_version, latest_plugin_version, repos, team, plan }
}
export async function getAgentUsage() {
  const { data } = await axios.get(`${memoryBase()}/me/agent-usage`, await withAuthSilent({ timeout: 15_000 }));
  return data; // { plan, plan_label, price_usd, used_pct, left_pct, resets_on, upgrade_url, memory: {...}, funnel: {...} }
}

// Which plan the agent draws from when the user holds both a personal plan and a team seat:
// "team" | "personal" | null (null = the default, the seat when there is one). Set on the user
// record; the gateway reads it within a minute.
export async function setMeterAgainst(meterAgainst) {
  const { data } = await axios.put(`${memoryBase()}/me/agent-settings`, { meter_against: meterAgainst }, await withAuth({ timeout: 15_000 }, { "Content-Type": "application/json" }));
  return data;
}
// The personal plan's own cancel (at period end; paid time kept) and resume — our buttons.
export async function cancelPersonalPlan() {
  const { data } = await axios.post(`${memoryBase()}/me/plan/cancel`, {}, await withAuth({ timeout: 20_000 }, { "Content-Type": "application/json" }));
  return data; // { ok, cancel_at_period_end, cancel_at }
}
// The personal plans for sale (from the live Stripe price map), which one this account holds, and a
// change: previewed first (new monthly price, prorated amount due today), then made in place.
// When there is no live subscription the change answers { checkout: true, price_id } and the page
// starts checkout for exactly that price.
export async function getPlans() {
  const { data } = await axios.get(`${memoryBase()}/me/plans`, await withAuthSilent({ timeout: 15_000 }));
  return data; // { plans: [{ plan, price_id, price_usd }], current, subscribed, cancel_at_period_end }
}
export async function previewPlanChange(plan) {
  const { data } = await axios.get(`${memoryBase()}/me/plan/preview`, await withAuth({ timeout: 20_000, params: { plan } }));
  return data; // { plan, price_id, monthly_after_usd, checkout, due_today_cents, period_end, lines }
}
export async function changePlan(plan) {
  const { data } = await axios.post(`${memoryBase()}/me/plan/change`, { plan }, await withAuth({ timeout: 30_000 }, { "Content-Type": "application/json" }));
  return data; // { ok, checkout, price_id?, plan }
}
export async function resumePersonalPlan() {
  const { data } = await axios.post(`${memoryBase()}/me/plan/resume`, {}, await withAuth({ timeout: 20_000 }, { "Content-Type": "application/json" }));
  return data;
}

// ============ Team plan (memory side) ============
// ONE plan per org. Seats are BOUGHT (the owner picks how many Standard / Premium — that is the
// bill), people are INVITED (owner or an admin; same org only; an email without an account is held
// pending), and a seat is ASSIGNED to a person. Only seat-holders are covered. Sharing a space is
// a team feature and is gated server-side.
export async function getTeamPlan() {
  const { data } = await axios.get(`${memoryBase()}/me/team`, await withAuthSilent({ timeout: 15_000 }));
  return data; // { active, exists, role, can_manage_people, can_manage_money, my_seat, members:[{uid,email,seat,role,is_owner,personal_plan}], pending:[{email,seat}], seats:{standard:{bought,assigned,free},premium:{...}}, seat_prices, configured, usage?, usage_scope? }
}
const JSON_HDR = { "Content-Type": "application/json" };
export async function previewTeamSeats(standard, premium, memory = 0) {
  const q = `standard=${standard}&premium=${premium}&memory=${memory}`;
  const { data } = await axios.get(`${memoryBase()}/me/team/seats/preview?${q}`, await withAuth({ timeout: 20_000 }));
  return data; // { seats_now, seats_after, monthly_now_usd, monthly_after_usd, active, due_today_cents|null, period_end, lines, preview_error? }
}
export async function setTeamSeats(standard, premium, memory = 0) {
  const { data } = await axios.post(`${memoryBase()}/me/team/seats`, { standard, premium, memory }, await withAuth({ timeout: 20_000 }, JSON_HDR));
  return data; // { ok, seats }
}
// The product a new account came for (the site's /start?product=), so it gets the right welcome.
export async function postSignupIntent(body) {
  const { data } = await axios.post(`${memoryBase()}/me/signup-intent`, body, await withAuthSilent({ timeout: 10_000 }, JSON_HDR));
  return data; // { ok, recorded }
}
export async function postAttribution(body) {
  const { data } = await axios.post(`${memoryBase()}/me/attribution`, body, await withAuthSilent({ timeout: 10_000 }, JSON_HDR));
  return data; // { ok, recorded }
}
export async function addTeamMember(email, seat = null) {
  const { data } = await axios.post(`${memoryBase()}/me/team/members`, { email, seat: seat || undefined }, await withAuth({ timeout: 15_000 }, JSON_HDR));
  return data; // { ok, uid | pending, seat, seats }
}
export async function removeTeamMember(uidOrEmail) {
  const body = uidOrEmail.includes("@") ? { email: uidOrEmail } : { uid: uidOrEmail };
  const { data } = await axios.post(`${memoryBase()}/me/team/members/remove`, body, await withAuth({ timeout: 15_000 }, JSON_HDR));
  return data;
}
export async function assignTeamSeat(uidOrEmail, seat) {
  const body = { ...(uidOrEmail.includes("@") ? { email: uidOrEmail } : { uid: uidOrEmail }), seat: seat || null };
  const { data } = await axios.post(`${memoryBase()}/me/team/assign`, body, await withAuth({ timeout: 15_000 }, JSON_HDR));
  return data; // { ok, seats }
}
export async function setTeamRole(uid, role) {
  const { data } = await axios.post(`${memoryBase()}/me/team/role`, { uid, role }, await withAuth({ timeout: 15_000 }, JSON_HDR));
  return data; // { ok, uid, role }
}

// Team settings (owner or admin): may people outside the company join its shared spaces?
export async function setTeamSettings(settings) {
  const { data } = await axios.post(`${memoryBase()}/me/team/settings`, settings, await withAuth({ timeout: 15_000 }, JSON_HDR));
  return data; // { ok, allow_external_guests }
}

// ============ Billing / Stripe ============
export async function createCheckoutSession(priceId, { interval = "month", returnTo = "/billing", product = "analytics", workspace, plan } = {}) {
  // price_id is honored when the backend knows it; otherwise the backend picks the default price
  // for `product` + `interval` (memory's once its prices exist, analytics Pro until then).
  // return_to lets the memory page start checkout and get its user back (/billing or /memory).
  const { data } = await axios.post(
    `${API_BASE}/create-checkout-session`,
    { price_id: priceId || undefined, plan: plan || undefined, interval, return_to: returnTo, product, workspace,
      origin: typeof window !== "undefined" ? window.location.origin : undefined },   // come back to THIS Studio (localhost, preview, prod)
    await withAuth({ timeout: 10_000 }, { "Content-Type": "application/json" })
  );
  if (!data?.url) throw new Error("No checkout URL returned");
  window.location.href = data.url;
}

// Stripe's own portal for the personal plan: change the card, see invoices. Comes back to /account.
// Asked of the memory service (it carries the Stripe key, like /me/plans), so this page depends only
// on the service its other calls use, not on the analytics API's release.
export async function openBillingPortal(returnTo = "/account") {
  const { data } = await axios.post(
    `${memoryBase()}/create-portal-session`, { return_to: returnTo },
    await withAuth({ timeout: 10_000 }, { "Content-Type": "application/json" })
  );
  if (!data?.url) throw new Error("No billing portal URL returned");
  window.location.href = data.url;
}

// ============ Sharing a space by link ============
// What an invite link shows before anyone signs in (public: the link is the secret).
export async function getInvitePreview(token) {
  const { data } = await axios.get(`${memoryBase()}/invites/${encodeURIComponent(token)}`, { timeout: 10_000 });
  return data; // { status: open|accepted|withdrawn|expired, space_name, inviter, member_count, expires_at, people?: [first names], summary?: "first paragraph" }
}
export async function acceptInvite(token) {
  const { data } = await axios.post(`${memoryBase()}/me/invites/${encodeURIComponent(token)}/accept`, {},
    await withAuth({ timeout: 15_000 }, { "Content-Type": "application/json" }));
  return data; // { workspace, space_id, name, joined }
}

// ============ Simulation ============
/**
 * Start simulation for a dictionary.
 * Returns { jobId, status } immediately (status = "queued").
 */
export async function startSimulation(dictId) {
  if (!dictId) throw new Error("dictId is required");
  const form = new FormData();
  form.append("dict_id", dictId);

  const { data } = await axios.post(
    `${API_BASE}/simulate`,
    form,
    await withAuth({ timeout: 10_000 })
  );
  return { jobId: data.job_id || data.jobId, status: data.status };
}

/**
 * Polls until dictionary has simulated_data_path.
 * Stops when either simulated_data_path exists or timeout reached.
 */
export async function waitForSimulation(dictId, { intervalMs = 5000, maxMs = 10 * 60 * 1000 } = {}) {
  const start = Date.now();
  while (true) {
    const dict = await getDictionary(dictId);
    if (dict.simulated_data_path) {
      return dict.simulated_data_path;
    }
    if (Date.now() - start > maxMs) {
      throw new Error("Simulation polling timed out");
    }
    await delay(intervalMs);
  }
}

// ============ Client Dataset (/data) ============

export async function stageClientCsv(file) {
  if (!file) throw new Error("file is required");
  const form = new FormData();
  form.append("file", file);
  try {
    const { data } = await axios.post(
      `${API_BASE}/data/stage`,
      form,
      await withAuth({ timeout: 60_000 })
    );
    return data; // { file_key, filename, schema, preview, row_count }
  } catch (err) {
    throw enrichAxiosError("[STAGE CLIENT CSV]", err);
  }
}

// A failed streaming request as an Error whose message is the sentence the backend sent (its
// `error` field), not the raw JSON. `upgrade` is set when the plan's allowance or data limit was
// the reason, so the screen can offer the upgrade.
export async function streamError(resp) {
  const text = await resp.text().catch(() => "");
  let body = null;
  try { body = JSON.parse(text); } catch (_) { /* not JSON */ }
  const err = new Error((body && body.error) || text || `HTTP ${resp.status}`);
  err.status = resp.status;
  err.upgrade = Boolean(body && body.upgrade);
  err.code = body && body.code;
  return err;
}

export async function sendDataChatMessage({ message, file_key }, onEvent) {
  const headers = await authHeaders({ "Content-Type": "application/json" });
  const resp = await fetch(`${API_BASE}/data/chat`, {
    method: "POST",
    headers,
    body: JSON.stringify({ message, file_key }),
  });
  if (!resp.ok) throw await streamError(resp);
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop();
    for (const line of lines) {
      if (line.startsWith("data: ")) {
        try { onEvent(JSON.parse(line.slice(6))); } catch {}
      }
    }
  }
}

export async function uploadClientCsv(file, tableName) {
  if (!file) throw new Error("file is required");
  const form = new FormData();
  form.append("file", file);
  if (tableName) form.append("table_name", tableName);
  try {
    const { data } = await axios.post(
      `${API_BASE}/data/upload`,
      form,
      await withAuth({ timeout: 120_000 })
    );
    return data;
  } catch (err) {
    throw enrichAxiosError("[UPLOAD CLIENT CSV]", err);
  }
}

export async function listClientTables() {
  const { data } = await axios.get(
    `${API_BASE}/data/tables`,
    await withAuth({ timeout: 15_000 })
  );
  return data;
}

export async function getClientTableRows(tableName, { limit = 100, offset = 0 } = {}) {
  const { data } = await axios.get(
    `${API_BASE}/data/table/${encodeURIComponent(tableName)}`,
    await withAuth({ params: { limit, offset }, timeout: 30_000 })
  );
  return data;
}

export async function deleteClientTable(tableName) {
  const { data } = await axios.delete(
    `${API_BASE}/data/table/${encodeURIComponent(tableName)}`,
    await withAuth({ timeout: 15_000 })
  );
  return data;
}

export async function listDataHistory() {
  const { data } = await axios.get(`${API_BASE}/data/history`, await withAuth({ timeout: 15_000 }));
  return data; // { history: [...] }
}

export async function getDataHistoryEntry(historyId) {
  const { data } = await axios.get(
    `${API_BASE}/data/history/${encodeURIComponent(historyId)}`,
    await withAuth({ timeout: 10_000 })
  );
  return data;
}

/**
 * High-level helper: simulate and wait until finished.
 */
export async function simulateDictionary(dictId) {
  if (!dictId) throw new Error("Dictionary ID required");
  const form = new FormData();
  form.append("dict_id", dictId);

  const { data } = await axios.post(
    `${API_BASE}/simulate`,
    form,
    await withAuth({ timeout: 30_000 })
  );

  // data looks like { status: "queued", job_id: "..." }
  return data;
}

// ============ GitHub Integration ============
export async function connectGitHub() {
  // Include frontend URL in request so backend knows where to redirect back
  const frontendUrl = window.location.origin;
  const { data } = await axios.post(
    `${API_BASE}/integrations/github/connect`,
    { frontend_url: frontendUrl },
    await withAuth()
  );
  return data; // { authorize_url }
}

export async function getGitHubStatus() {
  const { data } = await axios.get(`${API_BASE}/integrations/github/status`, await withAuth());
  return data; // { connected: true/false, github_login, connected_at }
}

export async function disconnectGitHub() {
  const { data } = await axios.post(`${API_BASE}/integrations/github/disconnect`, {}, await withAuth());
  return data;
}

export async function getGitHubRepositories() {
  const { data } = await axios.get(`${API_BASE}/integrations/github/repositories`, await withAuth());
  return data; // { repositories: [{ full_name, name, owner, private }] }
}

export async function getGitHubBranches(repoOwner, repoName) {
  const { data } = await axios.get(
    `${API_BASE}/integrations/github/repositories/${encodeURIComponent(repoOwner)}/${encodeURIComponent(repoName)}/branches`,
    await withAuth()
  );
  return data; // { branches: ["main", "develop", ...] }
}

export async function exportToGitHub({ runId, dictId, repoOwner, repoName, repoPath, branch = "main", type }) {
  const { data } = await axios.post(
    `${API_BASE}/integrations/github/export`,
    {
      run_id: runId,
      dict_id: dictId,
      repo_owner: repoOwner,
      repo_name: repoName,
      repo_path: repoPath,
      branch,
      type, // "notebook" or "dictionary"
    },
    await withAuth({ timeout: 30_000 })
  );
  return data;
}


// ============ Projects ============

// Silent twin of listProjects for places that must never trigger an auth redirect: the agent
// nav's "Analytics" entry (rendered on the public /connect) and the /start landing decision.
// Unauthenticated or failing reads resolve to [] — "no analytics" — rather than bouncing anyone.
export async function listProjectsSilent() {
  const { data } = await axios.get(`${API_BASE}/projects`, await withAuthSilent({ timeout: 15_000 }));
  return Array.isArray(data) ? data : (Array.isArray(data?.projects) ? data.projects : []);
}

export async function listProjects() {
  const { data } = await axios.get(`${API_BASE}/projects`, await withAuth({ timeout: 15_000 }));
  return data;
}

export async function createProject({ name, description = "", config = {} }) {
  const { data } = await axios.post(`${API_BASE}/projects`, { name, description, config }, await withAuth());
  return data;
}

export async function getProject(projectId) {
  const { data } = await axios.get(`${API_BASE}/projects/${encodeURIComponent(projectId)}`, await withAuth({ timeout: 15_000 }));
  return data;
}

export async function updateProject(projectId, updates) {
  const { data } = await axios.put(`${API_BASE}/projects/${encodeURIComponent(projectId)}`, updates, await withAuth());
  return data;
}

export async function deleteProject(projectId) {
  const { data } = await axios.delete(`${API_BASE}/projects/${encodeURIComponent(projectId)}`, await withAuth());
  return data;
}

export async function listProjectRuns(projectId) {
  const { data } = await axios.get(`${API_BASE}/projects/${encodeURIComponent(projectId)}/runs`, await withAuth({ timeout: 15_000 }));
  return data;
}

export async function listProjectSessions(projectId) {
  const { data } = await axios.get(`${API_BASE}/projects/${encodeURIComponent(projectId)}/sessions`, await withAuth({ timeout: 15_000 }));
  return data;
}

// ============ Project deployments + sharing ============

export async function deployProjectFromRun(projectId, sourceRunId) {
  const { data } = await axios.post(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/deploy-from-run`,
    { source_run_id: sourceRunId },
    await withAuth({ timeout: 300_000 })   // clone can take a couple minutes
  );
  return data;
}

export async function getProjectDeployment(projectId) {
  const { data } = await axios.get(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/deployment`,
    await withAuth({ timeout: 15_000 })
  );
  return data;
}

export async function listProjectDeployments(projectId) {
  const { data } = await axios.get(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/deployments`,
    await withAuth({ timeout: 15_000 })
  );
  return data;
}

export async function getProjectSharing(projectId) {
  const { data } = await axios.get(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/sharing`,
    await withAuth({ timeout: 15_000 })
  );
  return data;
}

export async function shareProject(projectId, { add = [], remove = [] } = {}) {
  const { data } = await axios.post(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/share`,
    { add, remove },
    await withAuth({ timeout: 15_000 })
  );
  return data;
}

export async function listOrgUsers(orgId) {
  // Silent-only: also called by ImpersonationProvider at mount (to rehydrate a view-as
  // selection after a reload), so it must not be able to bounce the page to sign-in.
  const { data } = await axios.get(
    `${API_BASE}/orgs/${encodeURIComponent(orgId)}/users`,
    await withAuthSilent({ timeout: 15_000 })
  );
  return data;
}

// ─── Model chat (per-deployment Q&A about the deployed model) ───────────────

export async function getModelChatThread(projectId) {
  const { data } = await axios.get(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/model-chat/thread`,
    await withAuth({ timeout: 15_000 })
  );
  return data;
}

/**
 * Stream a model-chat message via SSE. `onEvent` is called for each event:
 *   { type: "status",     text: "Querying your data…" }
 *   { type: "text_delta", text: "Based on the last…" }
 *   { type: "done",       messages: [...] }
 *   { type: "error",      error: "...", client_message: "..." }
 *
 * Returns a promise that resolves when the stream ends.
 */
export async function streamModelChatMessage(projectId, message, onEvent) {
  const headers = await authHeaders({ "Content-Type": "application/json" });
  const resp = await fetch(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/model-chat/messages`,
    {
      method: "POST",
      headers,
      body: JSON.stringify({ message }),
    }
  );
  if (!resp.ok) {
    let detail = "";
    try { detail = (await resp.json())?.error || ""; } catch (_) {}
    throw new Error(detail || `HTTP ${resp.status}`);
  }
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    // SSE events are separated by blank lines (\n\n). Within an event, lines
    // beginning with "data: " carry the payload.
    while (true) {
      const idx = buf.indexOf("\n\n");
      if (idx < 0) break;
      const chunk = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      const payload = chunk
        .split("\n")
        .filter((l) => l.startsWith("data:"))
        .map((l) => l.slice(5).trimStart())
        .join("\n");
      if (!payload) continue;
      try {
        onEvent(JSON.parse(payload));
      } catch (_) {
        /* swallow malformed events — the next \n\n will resync */
      }
    }
  }
}

// Live page (project-scoped). Resolves the right dataset by caller server-side.
export async function listProjectLiveTables(projectId) {
  const { data } = await axios.get(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/live/tables`,
    await withAuth({ timeout: 15_000 })
  );
  return data;
}

export async function getProjectLiveTableRows(projectId, tableName, { limit = 100, offset = 0 } = {}) {
  const { data } = await axios.get(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/live/table/${encodeURIComponent(tableName)}`,
    await withAuth({ params: { limit, offset }, timeout: 30_000 })
  );
  return data;
}

export async function getProjectLiveDataset(projectId) {
  const { data } = await axios.get(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/live/dataset`,
    await withAuth({ timeout: 10_000 })
  );
  return data;
}

// ============ Analyze (chat-driven analytical reports) ============

export async function listAnalyzeSessions() {
  const { data } = await axios.get(
    `${API_BASE}/analyze/sessions`,
    await withAuth({ timeout: 15_000 })
  );
  return data;
}

export async function renameAnalyzeSession(sessionId, name) {
  const { data } = await axios.patch(
    `${API_BASE}/analyze/sessions/${encodeURIComponent(sessionId)}`,
    { name },
    await withAuth()
  );
  return data;
}

export async function createAnalyzeSession(file, projectId = null, message = null, deferFirstTurn = false) {
  const form = new FormData();
  form.append("file", file);
  if (projectId) form.append("project_id", projectId);
  if (message) form.append("message", message);
  if (deferFirstTurn) form.append("defer_first_turn", "1");
  const { data } = await axios.post(
    `${API_BASE}/analyze/sessions`,
    form,
    await withAuth({ timeout: 120_000 })
  );
  return data;
}

/**
 * Create a new analyze session that references existing BigQuery tables.
 * `tables` is an array of { dataset, table } refs. `message` is the
 * user's first question (drives the agent's first turn if present).
 */
export async function createAnalyzeSessionFromTables(tables, projectId = null, message = null, deferFirstTurn = false) {
  const { data } = await axios.post(
    `${API_BASE}/analyze/sessions`,
    {
      source: "tables", tables,
      ...(projectId ? { project_id: projectId } : {}),
      ...(message ? { message } : {}),
      ...(deferFirstTurn ? { defer_first_turn: true } : {}),
    },
    await withAuth({ timeout: 120_000 })
  );
  return data;
}

/**
 * Create an analyze session with no data attached — the agent will help the
 * user figure out what data they need (goal mode).
 */
export async function createAnalyzeSessionFromGoal(message, projectId = null) {
  const { data } = await axios.post(
    `${API_BASE}/analyze/sessions`,
    { source: "goal", message, ...(projectId ? { project_id: projectId } : {}) },
    await withAuth({ timeout: 120_000 })
  );
  return data;
}

/** List BigQuery tables in the caller's org dataset (data page tables). */
export async function listDataTables() {
  const { data } = await axios.get(
    `${API_BASE}/data/tables`,
    await withAuth({ timeout: 15_000 })
  );
  return data; // { tables: [...], dataset }
}

export async function sendAnalyzeMessage(sessionId, message) {
  const { data } = await axios.post(
    `${API_BASE}/analyze/sessions/${encodeURIComponent(sessionId)}/chat`,
    { message },
    await withAuth({ timeout: 1_800_000 })
  );
  return data;
}

export async function sendAnalyzeMessageStreaming(sessionId, message, onEvent) {
  const headers = await authHeaders({ "Content-Type": "application/json" });
  const resp = await fetch(
    `${API_BASE}/analyze/sessions/${encodeURIComponent(sessionId)}/chat`,
    { method: "POST", headers, body: JSON.stringify({ message }) }
  );
  if (!resp.ok) throw await streamError(resp);
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop();
    for (const line of lines) {
      if (line.startsWith("data: ")) {
        try { onEvent(JSON.parse(line.slice(6))); } catch {}
      }
    }
  }
}

// Stream v2 ML build progress events. `onEvent(ev)` fires for every
// event the backend emits while the run is in flight; the stream closes
// when the run reaches a terminal status (complete / failed) or after
// a backend-imposed 10-min cap.
//
// Pass an AbortController.signal to allow the caller to cancel.
export async function streamV2RunEvents(runId, onEvent, { signal } = {}) {
  const headers = await authHeaders({});
  const resp = await fetch(
    `${API_BASE}/v2/runs/${encodeURIComponent(runId)}/events/stream`,
    { method: "GET", headers, signal }
  );
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new Error(text || `HTTP ${resp.status}`);
  }
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop();
    for (const line of lines) {
      if (line.startsWith("data: ")) {
        try { onEvent(JSON.parse(line.slice(6))); } catch {}
      }
    }
  }
}

// ─── Data readiness ─────────────────────────────────────────────────────────
// Fetch the readiness report for the active dataset. Returns:
//   { report: { modeling_score, deployment_score, findings, scored_at, ... },
//     from_cache: boolean }
// Pass refresh=true to bypass the 24h cache and force a fresh scan.
export async function getDataReadiness({ refresh = false } = {}) {
  const qs = refresh ? "?refresh=1" : "";
  const { data } = await axios.get(
    `${API_BASE}/data/readiness${qs}`,
    // Up to 5 min: with parallelized checks (8 workers in the composer)
    // even a 19-table dataset like cp-wealth finishes in ~60-90s. The
    // long timeout is headroom for the first uncached scan; cached
    // responses return in <1s.
    await withAuth({ timeout: 300_000 })
  );
  return data;
}

// Phase B endpoints (added there): preview + apply data fixes
export async function previewDataFix(actionPayload) {
  const { data } = await axios.post(
    `${API_BASE}/data/action/preview`,
    actionPayload,
    await withAuth({ timeout: 30_000 })
  );
  return data;
}

// Per-table column dictionary (curated + BQ-schema fallback). Returned shape:
//   { tables: [{name, row_count, has_curated_dict, description, columns: [
//     {name, type, is_pk, is_fk, fk_ref, description, role}
//   ]}] }
export async function getDataDictionary() {
  const { data } = await axios.get(
    `${API_BASE}/data/dictionary`,
    await withAuth({ timeout: 60_000 })
  );
  return data;
}

// Update one description in the data dictionary.
// Omit `column` to edit the table-level description.
// Backend: first-time edit on a non-curated table materializes a bare
// dict from the BQ schema before applying the change, so the change always
// lands. Cache is invalidated server-side so a subsequent /data/readiness
// fetch re-scores against the new dict.
export async function patchDataDictionary({ table, column, description }) {
  const { data } = await axios.patch(
    `${API_BASE}/data/dictionary`,
    { table, column, description },
    await withAuth({ timeout: 60_000 })
  );
  return data;
}

// Batch-update many descriptions for one table in a SINGLE request. The dict is
// one YAML object in GCS (≈1 mutation/sec limit), so "Accept all" must not fire
// N separate PATCHes — that trips a 429. `edits`: [{ column?, description }].
export async function patchDataDictionaryBatch({ table, edits }) {
  const { data } = await axios.patch(
    `${API_BASE}/data/dictionary`,
    { table, edits },
    await withAuth({ timeout: 60_000 })
  );
  return data;
}

// AI-draft descriptions for one table. Returns suggestions WITHOUT persisting
// — the user accepts (→ patchDataDictionary) or edits each one inline.
// Shape: { table, table_description, columns: [{name, description, key}] }
export async function generateDataDictionary({ table, columns } = {}) {
  const { data } = await axios.post(
    `${API_BASE}/data/dictionary/generate`,
    { table, columns },
    await withAuth({ timeout: 120_000 })
  );
  return data;
}

// ─── Deployment composer ─────────────────────────────────────────────────────

// Preview a deployment: composer authors + validates a spec without touching
// GCP. The response includes a narration the chat surface renders inline.
export async function previewProjectDeployment(projectId, body = {}) {
  const { data } = await axios.post(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/deploy/preview`,
    body,
    await withAuth({ timeout: 180_000 })  // includes one LLM call + dry-run
  );
  return data;
}

// Launch the deployment: re-author, re-validate, provision GCP. Returns the
// persisted spec.
export async function launchProjectDeployment(projectId, body = {}) {
  const { data } = await axios.post(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/deploy/launch`,
    body,
    await withAuth({ timeout: 180_000 })
  );
  return data;
}

// Read the persisted deployment spec (returns null if undeployed).
// Distinct from getProjectDeployment which hits the legacy /deployment GET.
export async function getProjectDeploymentSpec(projectId) {
  const { data } = await axios.get(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/deployment/spec`,
    await withAuth({ timeout: 30_000 })
  );
  return data;
}

// Render the live view right now (executes the composition against current
// data). The Live tab uses this for on-demand refresh.
export async function renderProjectLiveView(projectId) {
  const { data } = await axios.post(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/deployment/render`,
    {},
    await withAuth({ timeout: 180_000 })
  );
  return data;
}

/**
 * Re-query BigQuery and re-render the project's report in place.
 *
 * Distinct from renderProjectLiveView: that one runs a deployment's sections
 * and re-renders via the LLM, this one runs a deterministic template so the
 * layout is identical between refreshes. Returns the fresh html plus the age
 * of the underlying data, which is not the same as the render time.
 *
 * @param {string} projectId
 * @returns {Promise<{html: string, data_as_of: string, generated_at: string,
 *                    summary_rows: number, detail_rows: number, bytes: number}>}
 */
export async function refreshProjectReport(projectId) {
  const { data } = await axios.post(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/report/refresh`,
    {},
    await withAuth({ timeout: 120_000 })
  );
  return data;
}

// Manually re-extract the report's per-section code. Used as a backfill for
// sessions whose report predates the background extractor, OR to retry after
// an extractor failure. Synchronous — backend runs the LLM call inline.
export async function extractProjectReportSections(projectId) {
  const { data } = await axios.post(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/deployment/extract-sections`,
    {},
    await withAuth({ timeout: 180_000 })
  );
  return data;
}

// Chat-edit the deployment composition. Backend re-validates + versions
// the new composition; bad edits return 422 with the validation report.
export async function reviseProjectDeployment(projectId, instruction) {
  const { data } = await axios.post(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/deployment/revise`,
    { instruction },
    await withAuth({ timeout: 180_000 })
  );
  return data;
}

// List archived compositions (most recent first). Used for the rollback UI.
export async function listProjectDeploymentVersions(projectId) {
  const { data } = await axios.get(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/deployment/versions`,
    await withAuth({ timeout: 30_000 })
  );
  return data;
}

// Restore a previous composition version.
export async function rollbackProjectDeployment(projectId, composition_version) {
  const { data } = await axios.post(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/deployment/rollback`,
    { composition_version },
    await withAuth({ timeout: 30_000 })
  );
  return data;
}

// Kick off a fresh v2 ML run for the deployed model. Returns the new
// ml_run_id; promotion is a separate explicit step.
export async function retrainProjectDeployment(projectId, body = {}) {
  const { data } = await axios.post(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/deployment/retrain`,
    body,
    await withAuth({ timeout: 30_000 })
  );
  return data;
}

// Promote a completed ML run into the deployment. Re-validates the current
// composition against the new model; rejected if validation fails.
export async function promoteProjectDeploymentModel(projectId, ml_run_id) {
  const { data } = await axios.post(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/deployment/promote-model`,
    { ml_run_id },
    await withAuth({ timeout: 60_000 })
  );
  return data;
}

// Upload a CSV for a file_upload_scoring deployment. Backend loads the CSV
// into the project's upload table, then re-renders the composition and
// returns the resulting HTML.
export async function uploadProjectDeploymentCsv(projectId, file) {
  const form = new FormData();
  form.append("file", file);
  const cfg = await withAuth({ timeout: 180_000 });
  cfg.headers = { ...(cfg.headers || {}), "Content-Type": "multipart/form-data" };
  const { data } = await axios.post(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/deployment/upload`,
    form,
    cfg
  );
  return data;
}

// AI-generated one-paragraph dataset summary + 3 suggested business questions.
// Cached server-side per (org, dataset); cheap to call repeatedly.
// Shape: { summary, questions: string[], generated_at, from_cache }
export async function getDatasetSummary() {
  const { data } = await axios.post(
    `${API_BASE}/data/dictionary/summary`,
    {},
    await withAuth({ timeout: 120_000 })
  );
  return data;
}

export async function applyDataFix(actionPayload) {
  const { data } = await axios.post(
    `${API_BASE}/data/action/apply`,
    actionPayload,
    await withAuth({ timeout: 60_000 })
  );
  return data;
}


export async function getAnalyzeSession(sessionId) {
  const { data } = await axios.get(
    `${API_BASE}/analyze/sessions/${encodeURIComponent(sessionId)}`,
    await withAuth({ timeout: 30_000 })
  );
  return data;
}

export async function getSessionAnalyses(sessionId) {
  const { data } = await axios.get(
    `${API_BASE}/analyze/sessions/${encodeURIComponent(sessionId)}/analyses`,
    await withAuth({ timeout: 30_000 })
  );
  return data;
}

export async function composeProjectReport(projectId) {
  const { data } = await axios.post(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/compose-report`,
    {},
    await withAuth({ timeout: 120_000 })
  );
  return data;
}

// ============ Admin (impersonation) ============

// Returns { is_admin: bool, email: string } — safe for all users (no 403).
export async function getAdminMe() {
  // Bypass _actingOrgId for this call — we always want to know the real caller.
  // SILENT-ONLY on purpose: this is a UI feature-gate probe fired on every page load by
  // ImpersonationProvider, including on the standalone /memory and /connect pages. With the
  // redirecting bearer, one stale token here yanked the whole page to CIAM — which is how
  // signing in on /memory landed you on the analytics app. Failing quietly (and retrying)
  // is always the right answer for a probe.
  const token = await getBearerSilent();
  const { data } = await axios.get(`${API_BASE}/admin/me`, {
    headers: { Authorization: `Bearer ${token}` },
    timeout: 10_000,
  });
  return data;
}

// Admin only — returns { orgs: [{ org_id, domain, bq_dataset, created_at }, ...] }.
export async function listAdminOrgs() {
  // Silent-only for the same reason as getAdminMe: ImpersonationProvider calls this at mount
  // on every route, so it must never be able to navigate the page away.
  const token = await getBearerSilent();
  const { data } = await axios.get(`${API_BASE}/admin/orgs`, {
    headers: { Authorization: `Bearer ${token}` },
    timeout: 15_000,
  });
  return data;
}

// Admin only — Usage Monitor rollup. Returns { users: [...], generated_at }.
// Optional orgId scopes to one org. Real-caller scoped (no acting-org header).
export async function getUsersActivity(orgId) {
  const token = await getBearer();
  const q = orgId ? `?org=${encodeURIComponent(orgId)}` : "";
  const { data } = await axios.get(`${API_BASE}/admin/users/activity${q}`, {
    headers: { Authorization: `Bearer ${token}` },
    timeout: 30_000,
  });
  return data;
}

// Admin only — one user's rollup + activity timeline. Returns { user, events }.
export async function getUserActivity(userId) {
  const token = await getBearer();
  const { data } = await axios.get(
    `${API_BASE}/admin/users/${encodeURIComponent(userId)}/activity`,
    { headers: { Authorization: `Bearer ${token}` }, timeout: 30_000 }
  );
  return data;
}
// ============ Device sign-in (Assertion terminal agent / Mac app) ============
// Attach this account's API key to the device that showed `code`; the device's poll receives it once.
// True when a token can be had without any interaction. The account MSAL remembers can outlive
// the session behind it, so "signed in" by useIsAuthenticated is not "can call the API".
export async function canCallApiSilently() {
  try { await getBearerSilent(); return true; } catch (_) { return false; }
}

// No-code app sign-in: a one-time grant bound to the app's challenge (see utils/appSignin.js).
export async function grantAppSignIn(challenge, device) {
  const { data } = await axios.post(`${memoryBase()}/me/device/grant`, { challenge, device }, await withAuthSilent({ timeout: 15_000 }));
  return data; // { ok, code }
}

export async function approveDevice(code) {
  const { data } = await axios.post(`${memoryBase()}/me/device/approve`, { user_code: code }, await withAuthSilent({ timeout: 15_000 }));
  return data;
}
